import { ddLogger } from '@b-stock/fe-core/bstock-react'

import type { VisibleRowRange } from './ManifestContext'
import type { HonestGridApi } from './types'
import { ROW_HEIGHT } from './ui'

// The grid api members getVisibleRowRange reads. getGridOption is narrowed to
// the options it queries so callers can supply a plain object; HonestGridApi
// keeps the honest nullable getVerticalPixelRange.
export type RowRangeApi = Pick<
  HonestGridApi,
  'getDisplayedRowCount' | 'getVerticalPixelRange' | 'paginationGetCurrentPage'
> & {
  getGridOption: (
    key: 'pagination' | 'rowModelType'
  ) => boolean | string | undefined
}

// Compute the actually-visible row range, distinct from AG Grid's rendered
// range (which includes the offscreen rowBuffer). Returns null to leave the
// current range untouched. Three supported shapes:
export const getVisibleRowRange = (
  api: RowRangeApi,
  { domLayout, limit }: { domLayout: 'autoHeight' | 'normal'; limit: number }
): VisibleRowRange | null => {
  const pageOffset = api.paginationGetCurrentPage() * limit // 0 when unpaginated

  // Paginated: each page is a fixed [offset, offset + limit - 1] window, known
  // in closed form — no measurement needed. getVisibleStartEnd clamps the end
  // to total, so a short final/only page still reads correctly.
  if (api.getGridOption('pagination')) {
    return { firstRow: pageOffset, lastRow: pageOffset + limit - 1 }
  }

  // Unpaginated scroll viewport (fullscreen): derive the range from pixels.
  // getVerticalPixelRange() can return null/0-height before the viewport is
  // laid out, so we call this from multiple events to catch whichever one
  // fires after layout settles.
  if (domLayout === 'normal') {
    const pixelRange = api.getVerticalPixelRange()
    if (!pixelRange || pixelRange.bottom <= pixelRange.top) return null
    // Round both edges so a row counts only once it's at least half visible —
    // matching the scroll-depth analytics, not a 1px sliver at either end.
    return {
      firstRow: Math.round(pixelRange.top / ROW_HEIGHT) + pageOffset,
      lastRow: Math.round(pixelRange.bottom / ROW_HEIGHT) - 1 + pageOffset,
    }
  }

  // Unpaginated autoHeight: virtualization is off so every row is rendered and
  // visible. clientSide is the only row model that yields a trustworthy total
  // here (serverSide reports loading placeholders), so anything else is an
  // unsupported config we refuse rather than display a wrong count.
  if ((api.getGridOption('rowModelType') ?? 'clientSide') === 'clientSide') {
    // 0 before data loads or when client-side filtering yields nothing; skip
    // rather than report a -1 lastRow (which would render "Rows 1-0 of N").
    const rowCount = api.getDisplayedRowCount()
    if (rowCount === 0) return null
    return { firstRow: 0, lastRow: rowCount - 1 }
  }

  const message =
    'ManifestGrid: unpaginated autoHeight grid with a non-clientSide row model — cannot determine a trustworthy visible row range.'
  if (process.env.NODE_ENV === 'development') throw new Error(message)
  ddLogger.error(message)
  return null
}
